import { useState } from 'react'
import { Calendar, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { AggregationPeriod } from '@/lib/api/types'

interface DatePreset {
  label: string
  days: number
}

const DATE_PRESETS: DatePreset[] = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'Last year', days: 365 },
]

interface AnalyticsDateRangePickerProps {
  startDate: string
  endDate: string
  period: AggregationPeriod
  onDateChange: (startDate: string, endDate: string) => void
  onPeriodChange: (period: AggregationPeriod) => void
}

export function AnalyticsDateRangePicker({
  startDate,
  endDate,
  period,
  onDateChange,
  onPeriodChange,
}: AnalyticsDateRangePickerProps) {
  const [activePreset, setActivePreset] = useState<number | null>(30)

  const handlePresetClick = (days: number) => {
    setActivePreset(days)
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - days)
    const startStr = start.toISOString().split('T')[0] ?? ''
    const endStr = end.toISOString().split('T')[0] ?? ''
    onDateChange(startStr, endStr)
  }

  const formatDateRange = () => {
    if (!startDate || !endDate) return 'Select dates'
    const start = new Date(startDate)
    const end = new Date(endDate)
    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    return `${startStr} - ${endStr}`
  }

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
      {/* Date Presets */}
      <div className="flex items-center gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-800">
        {DATE_PRESETS.map((preset) => (
          <button
            key={preset.days}
            onClick={() => handlePresetClick(preset.days)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
              activePreset === preset.days
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>

      {/* Custom Date Range */}
      <Button
        variant="outline"
        className="h-9 px-3 gap-2 border-gray-200 dark:border-gray-700"
        onClick={() => setActivePreset(null)}
      >
        <Calendar className="h-4 w-4" />
        <span className="text-sm">{formatDateRange()}</span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </Button>

      {/* Period Selector */}
      <Select value={period} onValueChange={(v) => onPeriodChange(v as AggregationPeriod)}>
        <SelectTrigger className="w-[130px] h-9">
          <SelectValue placeholder="Period" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="Hourly">Hourly</SelectItem>
          <SelectItem value="Daily">Daily</SelectItem>
          <SelectItem value="Weekly">Weekly</SelectItem>
          <SelectItem value="Monthly">Monthly</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}
